import { TableRow, TableCell } from '@mui/material';
import { AnyPlan } from '../../../types';

type OfflineBudgetGRPTableProps = {
  plans: AnyPlan[];
  copyName: string;
  formatBudget: (v?: number, currency?: string) => string;
  showOnlyDifferences: boolean;
  budgetValues: (number | undefined)[];
  budgetCurrencies: (string | undefined)[];
  grpValues: (number | undefined)[];
  splitValues: (number | undefined)[];
  weeksValues: (number | undefined)[];
};

export default function OfflineBudgetGRPTable({
  plans,
  copyName,
  formatBudget,
  showOnlyDifferences,
  budgetValues,
  budgetCurrencies,
  grpValues,
  splitValues,
  weeksValues,
}: OfflineBudgetGRPTableProps) {
  function isDifferentFromBase<T>(
    values: (T | undefined | null)[],
    index: number,
    equals: (a: T, b: T) => boolean = (a, b) => a === b
  ): boolean {
    if (index === 0 || values.length === 0) return false;
    const base = values[0];
    const current = values[index];

    if (base == null && current == null) return false;
    if ((base == null) !== (current == null)) return true;

    return !equals(base as T, current as T);
  }

  function hasAnyDifference<T>(
    values: (T | undefined | null)[],
    equals?: (a: T, b: T) => boolean
  ): boolean {
    for (let i = 1; i < values.length; i++) {
      if (isDifferentFromBase(values, i, equals as any)) return true;
    }
    return false;
  }

  const hasBudgetDiff = hasAnyDifference<number | undefined>(budgetValues);
  const hasGrpDiff = hasAnyDifference<number | undefined>(grpValues);
  const hasSplitDiff = hasAnyDifference<number | undefined>(splitValues);
  const hasWeeksDiff = hasAnyDifference<number | undefined>(weeksValues);

  const showAnyRow =
    hasBudgetDiff || hasGrpDiff || hasSplitDiff || hasWeeksDiff;

  if (showOnlyDifferences && !showAnyRow) return null;

  return (
    <>
      {/* Copy / budget row */}
      {(!showOnlyDifferences || hasBudgetDiff) && (
        <TableRow
          sx={{
            height: '40px',
            border: '1px solid',
            borderColor: '#C7D8F9',
          }}
        >
          <TableCell
            sx={{
              pl: 4,
              fontWeight: 600,
              fontSize: '13px',
              borderRight: '1px solid',
              borderColor: '#C7D8F9',
            }}
          >
            {copyName}
          </TableCell>
          {plans.map((plan, index) => {
            const value = budgetValues[index];
            const currency = budgetCurrencies[index];
            const diff = isDifferentFromBase<number | undefined>(
              budgetValues,
              index
            );

            return (
              <TableCell
                key={`${copyName}-budget-${plan.id}`}
                sx={{
                  borderLeft: '1px solid',
                  borderColor: '#C7D8F9',
                  textAlign: 'center',
                  fontWeight: 600,
                  fontSize: '13px',
                  color: diff ? 'error.main' : '#525F81',
                }}
              >
                {typeof value === 'number'
                  ? formatBudget(value, currency)
                  : 'N/A'}
              </TableCell>
            );
          })}
        </TableRow>
      )}

      {/* GRP row */}
      {(!showOnlyDifferences || hasGrpDiff) && (
        <TableRow
          sx={{
            height: '32px',
            border: '1px solid',
            borderColor: '#C7D8F9',
          }}
        >
          <TableCell
            sx={{
              pl: 4,
              fontWeight: 400,
              fontSize: '12px',
              borderRight: '1px solid',
              borderColor: '#C7D8F9',
            }}
          >
            GRP
          </TableCell>
          {plans.map((plan, index) => {
            const value = grpValues[index];
            const diff = isDifferentFromBase<number | undefined>(
              grpValues,
              index
            );

            return (
              <TableCell
                key={`${copyName}-grp-${plan.id}`}
                sx={{
                  borderLeft: '1px solid',
                  borderColor: '#C7D8F9',
                  textAlign: 'center',
                  fontWeight: 400,
                  fontSize: '12px',
                  color: diff ? 'error.main' : '#525F81',
                }}
              >
                {value != null ? value : 'N/A'}
              </TableCell>
            );
          })}
        </TableRow>
      )}

      {/* % Split row */}
      {(!showOnlyDifferences || hasSplitDiff) && (
        <TableRow
          sx={{
            height: '32px',
            border: '1px solid',
            borderColor: '#C7D8F9',
          }}
        >
          <TableCell
            sx={{
              pl: 4,
              fontWeight: 400,
              fontSize: '12px',
              borderRight: '1px solid',
              borderColor: '#C7D8F9',
            }}
          >
            % Split
          </TableCell>
          {plans.map((plan, index) => {
            const value = splitValues[index];
            const diff = isDifferentFromBase<number | undefined>(
              splitValues,
              index
            );

            return (
              <TableCell
                key={`${copyName}-split-${plan.id}`}
                sx={{
                  borderLeft: '1px solid',
                  borderColor: '#C7D8F9',
                  textAlign: 'center',
                  fontWeight: 400,
                  fontSize: '12px',
                  color: diff ? 'error.main' : '#525F81',
                }}
              >
                {typeof value === 'number'
                  ? `${(value * 100).toFixed(0)}%`
                  : 'N/A'}
              </TableCell>
            );
          })}
        </TableRow>
      )}

      {/* Weeks row */}
      {(!showOnlyDifferences || hasWeeksDiff) && (
        <TableRow
          sx={{
            height: '32px',
            border: '1px solid',
            borderColor: '#C7D8F9',
          }}
        >
          <TableCell
            sx={{
              pl: 4,
              fontWeight: 400,
              fontSize: '12px',
              borderRight: '1px solid',
              borderColor: '#C7D8F9',
            }}
          >
            Weeks
          </TableCell>
          {plans.map((plan, index) => {
            const value = weeksValues[index];
            const diff = isDifferentFromBase<number | undefined>(
              weeksValues,
              index
            );

            return (
              <TableCell
                key={`${copyName}-weeks-${plan.id}`}
                sx={{
                  borderLeft: '1px solid',
                  borderColor: '#C7D8F9',
                  textAlign: 'center',
                  fontWeight: 400,
                  fontSize: '12px',
                  color: diff ? 'error.main' : '#525F81',
                }}
              >
                {value != null ? value : 'N/A'}
              </TableCell>
            );
          })}
        </TableRow>
      )}
    </>
  );
}
